import {
  collection,
  query,
  orderBy,
  where,
  startAfter,
  getDocs,
  getCountFromServer,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  QueryDocumentSnapshot,
  DocumentData,
  limit,
} from 'firebase/firestore';
import { getDb } from './firebase';
import type { Supplier } from '../types';

const COLLECTION = 'suppliers';

export interface ListSuppliersParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  searchQuery?: string;
  districtFilter?: string;
}

export interface ListResult<T> {
  items: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

function toSupplier(snap: QueryDocumentSnapshot<DocumentData>): Supplier {
  const data = snap.data();
  return {
    id: snap.id,
    name: data.name,
    operatingLocation: data.operatingLocation ?? { district: null, county: null, parish: null },
    servicesOffered: data.servicesOffered ?? [],
    rating: data.rating,
    contactEmail: data.contactEmail,
    contactPhone: data.contactPhone,
  };
}

/**
 * List suppliers with optional district filter and cursor-based pagination.
 *
 * NOTE: Name search is performed in-memory after the Firestore fetch because
 * the Supplier type does not have a `searchKeywords` field, making
 * server-side full-text filtering unavailable.
 */
export async function listSuppliers({
  pageSize = 50,
  lastDoc: lastDocCursor = null,
  searchQuery = '',
  districtFilter = '',
}: ListSuppliersParams = {}): Promise<ListResult<Supplier>> {
  // Build query constraints
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const constraints: any[] = [orderBy('name', 'asc')];

  if (districtFilter) {
    constraints.push(where('operatingLocation.district', '==', districtFilter));
  }

  if (lastDocCursor) {
    constraints.push(startAfter(lastDocCursor));
  }

  // Fetch one extra to detect if there are more pages
  constraints.push(limit(pageSize + 1));

  const q = query(collection(getDb(), COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

  let items = pageDocs.map(toSupplier);

  // In-memory name search (acknowledged limitation — no searchKeywords on Supplier)
  if (searchQuery.trim()) {
    const lower = searchQuery.trim().toLowerCase();
    items = items.filter(s => s.name.toLowerCase().includes(lower));
  }

  return {
    items,
    lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    hasMore,
  };
}

/**
 * Create a new supplier. Returns the generated document ID.
 * Supplier has no createdAt/updatedAt fields by design.
 */
export async function createSupplier(data: Omit<Supplier, 'id'>): Promise<string> {
  const docRef = await addDoc(collection(getDb(), COLLECTION), {
    name: data.name,
    operatingLocation: data.operatingLocation,
    servicesOffered: data.servicesOffered,
    ...(data.rating !== undefined && { rating: data.rating }),
    ...(data.contactEmail && { contactEmail: data.contactEmail }),
    ...(data.contactPhone && { contactPhone: data.contactPhone }),
  });
  return docRef.id;
}

/**
 * Update an existing supplier's mutable fields.
 */
export async function updateSupplier(
  id: string,
  data: Partial<Omit<Supplier, 'id'>>
): Promise<void> {
  await updateDoc(doc(getDb(), COLLECTION, id), { ...data });
}

/**
 * Delete a supplier document.
 */
export async function deleteSupplier(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), COLLECTION, id));
}

export async function countSuppliers(): Promise<number> {
  const snap = await getCountFromServer(collection(getDb(), COLLECTION));
  return snap.data().count;
}
